import React, { useState, useEffect } from 'react'
import { IAttendee, IEpiProps, IEventRegistrationRefreshModel, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { DateTimeCard } from './date-time-card'
import { matchUserProgramToSpecialtyProgram, formatLocationDisplay } from '../helpers/helpers'
import { ProductBasedAddonCard } from './product-based-addon-card'
import { ProgramSummary } from './program-summary'
import { AttendeesReview } from './attendees-review'
import { MainContent } from './layout/main-content'
import { SideBarContent } from './layout/sidebar-content'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

export const ShareSelections = () => {
  const { currentStep, epiProps, userSelections, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!
  
  const [shareLink, setShareLink] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [isCopied, setIsCopied] = useState<boolean>(false)

  //Get specialty program details
  const specialtyProgram = matchUserProgramToSpecialtyProgram(
    userSelections.userProgramSelection,
    eventRegistrationRefreshModel
  );

  // Generate share link on component mount
  useEffect(() => {
    generateShareLink();
  }, []);

  const generateShareLink = async () => {
    setIsGenerating(true);
    setError('');
    setIsCopied(false);
    
    try {
      const requestData = {
        userSelections: JSON.stringify(userSelections),
        currentStep: currentStep,
        set24HourExpiration: userSelections?.attendees != null && userSelections.attendees.length > 0 && userSelections.attendees[0].firstName && userSelections.attendees[0].firstName.length > 0, //Set 24 hour expiration if share contains attendee information.
      };

      const response = await fetch('/internalapi/EventRegistrationRefreshApi/SaveSharedSelection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();
      
      if (result.isError) {
        setError(epiProps.generatingLinkErrorMessage);
        return;
      }

      const baseUrl = `${window.location.origin}${window.location.pathname}`;
      const generatedShareLink = `${baseUrl}?share=${result.shareId}`;
      setShareLink(generatedShareLink);

    } catch (err) {
      console.error('Error generating share link:', err);
      setError(epiProps.generatingLinkErrorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000); // Reset after 3 seconds
    } catch (err) {
      console.error('Failed to copy link:', err);
      setError(epiProps.failedToCopyLinkMessage);
    }
  };

  // Helper to format month/year dates for display
  const formatMonthYear = (dateString: string): string => {
    try {
      // Dates are stored in MM/YYYY format (e.g., "1/2025" for January 2025)
      const [month, year] = dateString.split('/');
      
      if (!month || !year) {
        return dateString;
      }
      
      // Create a date object from the month and year
      // Month is 0-indexed in JavaScript Date, so subtract 1
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    } catch {
      return dateString;
    }
  };

  // Helper to check if any filters are applied
  const hasActiveFilters = (): boolean => {
    const filters = userSelections?.filters;
    if (!filters) return false;
    
    return !!(
      (filters.cityState && filters.cityState.trim() !== '') ||
      (filters.postalCode && filters.postalCode.trim() !== '') ||
      (filters.rangeInput && filters.rangeInput.trim() !== '') ||
      (filters.selectedDates && filters.selectedDates.length > 0)
    );
  };

  return (
    <div>
      
      {/* Share Link Section */}
      <div className="share-modal-link-form">
        {isGenerating && (
          <p>{epiProps.generatingLinkMessage}</p>
        )}
        
        {!isGenerating && shareLink && (
            <div>
              <div className="form-item">
              <input 
                type="text" 
                value={shareLink} 
                readOnly 
                className="share-link-input"
              />
              </div>
              <button 
                className="btn-secondary form-item" 
                onClick={copyShareLink}
              >
                {isCopied ? epiProps.linkCopiedCtaText : epiProps.copyLinkCtaText}
              </button>
            </div>
        )}
        
        {error && (
          <p>{error}</p>
        )}
      </div>

      {/* Applied Filters Section */}
      {hasActiveFilters() && (
        <div className="card card-grey" style={{ marginBottom: '1.5rem' }}>
          <div className="card-section">
            <h2 className="card-header-title">{epiProps.appliedFiltersHeading || 'Applied Filters'}</h2>
            <div className="stack-sm">
              {((userSelections.filters?.cityState && userSelections.filters.cityState.trim() !== '') ||
                (userSelections.filters?.postalCode && userSelections.filters.postalCode.trim() !== '')) && (
                <div className="filter-display-item" style={{ marginBottom: '0.5rem' }}>
                  <strong>{epiProps.locationInputLabel || 'Location'}</strong>{' '}
                  {formatLocationDisplay(
                    userSelections.filters?.cityState ?? '',
                    userSelections.filters?.postalCode ?? ''
                  )}
                  {userSelections.filters?.rangeInput && userSelections.filters.rangeInput.trim() !== '' && (
                    <span> ({userSelections.filters.rangeInput} {epiProps.distanceFromLocationText || 'miles'})</span>
                  )}
                </div>
              )}
              {userSelections.filters?.selectedDates && userSelections.filters.selectedDates.length > 0 && (
                <div className="filter-display-item" style={{ marginBottom: '0.5rem' }}>
                  <strong>{epiProps.monthYearFilterHeader || 'Month/Year'}</strong>{' '}
                  {userSelections.filters.selectedDates.map((date, index) => (
                    <span key={date}>
                      {formatMonthYear(date)}
                      {index < userSelections.filters!.selectedDates!.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
              {userSelections.filters?.sortByDate !== undefined && (
                <div className="filter-display-item" style={{ marginBottom: '0.5rem' }}>
                  <strong>{epiProps.sortResultsByText || 'Sort Results By'}</strong>{' '}
                  {userSelections.filters.sortByDate ? (epiProps.sortByDateText || 'Date') : (epiProps.sortByLocationText || 'Location')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="app-page-content-layout">
        <MainContent>
          <AttendeesReview showEditCta={false} />
          <div className="card">
            {userSelections.userProgramSelection && (
              <div className="card-section">
                <h2 className="card-header-title">{userSelections.userProgramSelection.programName}</h2>

                {userSelections.userProgramDateTimeSelection && (
                  <>
                    <DateTimeCard
                      dateTimeEvent={userSelections.userProgramDateTimeSelection}
                      isSelected={true}
                      onSelect={() => { }} // No selection needed in review
                      isReviewStep={false}
                      isAddOn={false}
                      isShareSelections={true}
                    />
                  </>
                )}
              </div>
            )}

            {(userSelections.userAddOnProgramSelections || []).map((sel) => {
              const addonDef = specialtyProgram?.addOnPrograms?.find(a =>
                sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
              )
              return (
                <div key={sel.programSku || sel.programCode} className="card-section">
                  <h2 className="card-header-title">{sel.programName}</h2>
                  {!sel.disableDateTimeSelection && userSelections.userAddOnProgramDateTimeSelection && (
                    <DateTimeCard
                      dateTimeEvent={userSelections.userAddOnProgramDateTimeSelection}
                      isSelected={true}
                      onSelect={() => { }}
                      isReviewStep={false}
                      isAddOn={true}
                      isShareSelections={true}
                    />
                  )}
                  {sel.disableDateTimeSelection && addonDef && (
                    <ProductBasedAddonCard
                      program={addonDef}
                      showPrice={eventRegistrationRefreshModel.showPrice}
                      currency={eventRegistrationRefreshModel.currency}
                      isReviewMode={true}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </MainContent>
        <SideBarContent>
          <ProgramSummary
            userSelections={userSelections}
            onContinue={() => { }} // No continue needed in 'share selections' modal 
            showValidationMessage={false}
            validationMessage={""}
            hideContinueButton={true}
          />
        </SideBarContent>
      </div>
    </div>
  )
  
}
